import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaShieldAlt, FaCopyright, FaFileContract, FaExclamationTriangle, FaExternalLinkAlt } from 'react-icons/fa';
import HtmlContent, { processHtmlContent } from './HtmlContent';

export { processHtmlContent };

const LEGAL_TABS = [
  { id: 'privacy', label: 'Privacy Policy', path: '/privacy-policy', icon: FaShieldAlt },
  { id: 'copyright', label: 'Copyright & Open Access', path: '/copyright', icon: FaCopyright },
  { id: 'terms', label: 'Terms of Service', path: '/terms-of-service', icon: FaFileContract },
  { id: 'disclaimer', label: 'Medical Disclaimer', path: '/disclaimer', icon: FaExclamationTriangle },
];


const LegalPageLayout = ({ title, subtitle, content, loading, error, activePolicy }) => {
  const location = useLocation();

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans py-10 sm:py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Breadcrumb & Regulatory Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Link to="/" className="hover:text-[#03478E] transition">Home</Link>
            <span>/</span>
            <span>Legal &amp; Regulatory Policies</span>
            <span>/</span>
            <span className="text-[#03478E] font-semibold">{title}</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
            <div>
              <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
                LEGAL &amp; PUBLICATION COMPLIANCE
              </span>
              <h1 className="page-title">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-1">
                  {subtitle}
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 bg-white border border-gray-200 px-3.5 py-2 rounded-xl shadow-2xs self-start sm:self-auto">
              <span className="font-semibold text-gray-700 block">AIMJHM Official Policy</span>
            </div>
          </div>
        </div>

        {/* Policy Tab Switcher */}
        <div className="bg-white rounded-2xl p-2 border border-gray-200/80 shadow-xs flex flex-wrap gap-1.5">
          {LEGAL_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path || activePolicy === tab.id;
            return (
              <Link
                key={tab.id}
                to={tab.path}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition ${
                  isActive
                    ? 'bg-[#03478E] text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                }`}
              >
                <Icon className={`text-xs ${isActive ? 'text-blue-200' : 'text-[#03478E]'}`} />
                <span>{tab.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-sm border border-gray-200/80">
          {loading ? (
            <div className="py-16 text-center space-y-3">
              <div className="w-8 h-8 border-3 border-[#03478E] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-gray-400 font-medium">Loading live policy content...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs">
              {error}
            </div>
          ) : (
            <div className="legal-prose-content">
              <HtmlContent
                content={content}
                className="body-text space-y-4"
              />
            </div>
          )}
        </div>

        {/* Bottom Policy Notice */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-gray-500">
          <div>
            <span className="font-semibold text-gray-700">Need legal clarification or permissions?</span>
            <p className="text-xs text-gray-400 mt-0.5">
              Contact the editorial office at <a href="mailto:editor@aimjhm.com" className="text-[#03478E] font-medium hover:underline">editor@aimjhm.com</a> or <a href="mailto:domus.publications@gmail.com" className="text-[#03478E] font-medium hover:underline">domus.publications@gmail.com</a>
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#0B3B60] font-bold text-xs rounded-xl transition"
          >
            <span>Contact Editorial Office</span>
            <FaExternalLinkAlt className="text-[10px]" />
          </Link>
        </div>

      </div>

      <style>{`
        .legal-prose-content h1,
        .legal-prose-content h2,
        .legal-prose-content h3,
        .legal-prose-content h4 {
          color: #444444;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .legal-prose-content h1 { font-size: 1.5rem; }
        .legal-prose-content h2 { font-size: 1.25rem; }
        .legal-prose-content h3 { font-size: 1.125rem; }
        .legal-prose-content h4 { font-size: 1rem; }
        .legal-prose-content p {
          margin-bottom: 0.85rem;
          line-height: 1.7;
          color: #374151;
        }
        .legal-prose-content ul,
        .legal-prose-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
          list-style-type: disc;
          color: #374151;
        }
        .legal-prose-content li {
          margin-bottom: 0.4rem;
          line-height: 1.6;
        }
        .legal-prose-content strong {
          color: #0B3B60;
          font-weight: 600;
        }
        .legal-prose-content a {
          color: #03478E;
          text-decoration: underline;
        }
        .legal-prose-content a:hover {
          color: #022b54;
        }
      `}</style>
    </div>
  );
};

export default LegalPageLayout;
